import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";
import api from "@/lib/api";

// Shared constants + presentational color picker used by the Takeoff Studio and its dialogs.
export const COLORS = ["#1F5BFF", "#0B2A6F", "#E6194B", "#800000", "#F58231", "#FFC20E", "#8FD400", "#1B9E3F", "#008C8C", "#00B8F0", "#7B2CBF", "#F032E6", "#FF8FB1", "#8B5A2B", "#7F7F7F", "#111111"];
export const SYMBOLS = ["circle", "square", "triangle", "diamond", "hollow_circle", "hollow_square", "hexagon", "star", "cross"];
export const LINE_STYLES = ["solid", "dashed", "dotted"];
export const HATCHES = ["none", "solid", "diagonal", "crosshatch", "dots"];
export const KINDS = ["pile", "pre_drill", "row", "torque_tube_lf", "module", "string", "home_run", "combiner", "feeder_lf", "trench_lf", "conduit_lf", "cable_lf", "dc_conductor_lf", "ac_conductor_lf", "mv_cable_lf", "termination", "mv_splice", "skid", "inverter", "transformer", "pad", "ground_lf", "ground_rod", "fence_lf", "fiber_lf", "test", "commissioning", "handling", "qa_qc", "weather", "survey", "other"];
export const kindLabel = (k) => { const s = (k || "").replace(/_lf$/, " (ft)").replace(/_/g, " "); return s.charAt(0).toUpperCase() + s.slice(1); };
export const dashFor = (style, w = 2) => (style === "dashed" ? `${w * 4} ${w * 3}` : style === "dotted" ? `${Math.max(1, w)} ${w * 2.5}` : undefined);
let _legendCache = null;
// The company takeoff legend (kind -> colour, symbol, line style, width, hatch), fetched once per page load.
export function useTakeoffLegend() {
  const [legend, setLegend] = useState(_legendCache);
  useEffect(() => {
    if (_legendCache) return;
    api.get("/settings/takeoff-legend").then((r) => { _legendCache = r.data.legend; setLegend(_legendCache); }).catch(() => {});
  }, []);
  return legend || {};
}
export const setLegendCache = (l) => { _legendCache = l; };
// Legend look for a kind, as takeoff-layer fields.
export const legendLook = (legend, kind) => {
  const e = (legend || {})[kind];
  if (!e) return null;
  return { color: e.color, symbol: e.symbol, line_style: e.line_style, width: Number(e.width) || 2, hatch: e.hatch, measure_type: e.measure_type || null };
};
// Small preview of a layer's look (symbol for counts, line for lengths, hatch for areas).
export function LookSwatch({ look, type, testid }) {
  const c = look?.color || "#003E87";
  const mt = type || look?.measure_type || "count";
  if (mt === "linear") return <svg width="26" height="12" data-testid={testid}><line x1="1" y1="6" x2="25" y2="6" stroke={c} strokeWidth={Math.min(6, Number(look?.width) || 2)} strokeDasharray={dashFor(look?.line_style, Math.min(3, Number(look?.width) || 2))} /></svg>;
  if (mt === "area") return <svg width="18" height="14" data-testid={testid}><defs><pattern id={`sw-${testid}`} width="4" height="4" patternUnits="userSpaceOnUse"><path d="M0,4 L4,0" stroke={c} strokeWidth="1" /></pattern></defs><rect x="1" y="1" width="16" height="12" fill={look?.hatch === "none" ? "none" : look?.hatch === "solid" ? c : `url(#sw-${testid})`} stroke={c} strokeWidth="1.5" /></svg>;
  return <svg width="16" height="16" viewBox="-8 -8 16 16" data-testid={testid}>{glyphShape(look?.symbol || "circle", 0, 0, 6, c)}</svg>;
}
// One count glyph centred on (x,y) with radius r. Hollow shapes draw outline only.
export function glyphShape(symbol, x, y, r, col, sw = 1.5) {
  const fill = { fill: col, stroke: "#fff", strokeWidth: sw };
  const hollow = { fill: "#fff", fillOpacity: 0.6, stroke: col, strokeWidth: Math.max(sw, r * 0.35) };
  if (symbol === "square") return <rect x={x - r} y={y - r} width={r * 2} height={r * 2} {...fill} />;
  if (symbol === "hollow_square") return <rect x={x - r} y={y - r} width={r * 2} height={r * 2} {...hollow} />;
  if (symbol === "hollow_circle") return <circle cx={x} cy={y} r={r} {...hollow} />;
  if (symbol === "diamond") return <polygon points={`${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}`} {...fill} />;
  if (symbol === "triangle") return <polygon points={`${x},${y - r} ${x + r},${y + r} ${x - r},${y + r}`} {...fill} />;
  if (symbol === "hexagon") return <polygon points={[0, 1, 2, 3, 4, 5].map((i) => `${x + r * Math.cos(Math.PI / 3 * i)},${y + r * Math.sin(Math.PI / 3 * i)}`).join(" ")} {...fill} />;
  if (symbol === "star") return <polygon points={Array.from({ length: 10 }, (_, i) => { const rr = i % 2 ? r * 0.45 : r * 1.1; const a = -Math.PI / 2 + i * Math.PI / 5; return `${x + rr * Math.cos(a)},${y + rr * Math.sin(a)}`; }).join(" ")} {...fill} />;
  if (symbol === "cross") { const t = r * 0.38; return <polygon points={`${x - t},${y - r} ${x + t},${y - r} ${x + t},${y - t} ${x + r},${y - t} ${x + r},${y + t} ${x + t},${y + t} ${x + t},${y + r} ${x - t},${y + r} ${x - t},${y + t} ${x - r},${y + t} ${x - r},${y - t} ${x - t},${y - t}`} {...fill} />; }
  return <circle cx={x} cy={y} r={r} {...fill} />;
}
export const SIZES = { sm: 5, md: 7, lg: 10 };
export const WIDTHS = [1, 2, 3, 5, 8];
export const UNIT_TO_FT = { ft: 1, in: 1 / 12, yd: 3, m: 3.28084 };
export const ARCH_SCALES = [
  ["1/128\" = 1'", 1 / 128], ["1/64\" = 1'", 1 / 64], ["1/32\" = 1'", 1 / 32], ["1/16\" = 1'", 1 / 16],
  ["3/32\" = 1'", 3 / 32], ["1/8\" = 1'", 1 / 8], ["3/16\" = 1'", 3 / 16], ["1/4\" = 1'", 1 / 4],
  ["3/8\" = 1'", 3 / 8], ["1/2\" = 1'", 1 / 2], ["3/4\" = 1'", 3 / 4], ["1\" = 1'", 1],
  ["1 1/2\" = 1'", 1.5], ["3\" = 1'", 3],
];
export const CIVIL_SCALES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 300, 500, 1000].map((y) => [`1" = ${y}'`, 1 / y]);
export const UOM_OF = { count: "ea", linear: "ft", area: "sf" };

export function ColorPicker({ value, onChange, testid, size = "md", align = "left", drop = "down" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "#003E87");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  useEffect(() => { setDraft(value || "#003E87"); }, [value]);
  const commit = () => {
    const v = (draft || "").trim();
    if (/^#[0-9a-fA-F]{6}$/.test(v) && v.toLowerCase() !== (value || "").toLowerCase()) onChange(v);
  };
  // Position the popover in a body portal (fixed), clamped to the viewport, so it can NEVER be
  // clipped by the narrow tool rail / canvas scroll containers around it.
  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const W = 188, H = 236, GAP = 6, M = 8;
    let left = align === "right" ? r.right - W : r.left;
    let top = drop === "up" ? r.top - H - GAP : r.bottom + GAP;
    if (drop !== "up" && top + H > window.innerHeight - M && r.top - H - GAP > M) top = r.top - H - GAP;
    if (drop === "up" && top < M && r.bottom + H + GAP < window.innerHeight - M) top = r.bottom + GAP;
    left = Math.max(M, Math.min(left, window.innerWidth - W - M));
    top = Math.max(M, Math.min(top, window.innerHeight - H - M));
    setPos({ left, top });
  }, [align, drop]);
  useLayoutEffect(() => { if (open) place(); }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const reposition = () => place();
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      commit(); setOpen(false);
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, place, draft, value]); // eslint-disable-line react-hooks/exhaustive-deps
  const dim = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-6 h-6" : "w-5 h-5";
  return (
    <div className="relative inline-block">
      <button type="button" ref={btnRef} data-testid={testid} title="Pick any color" onClick={() => setOpen((o) => !o)}
        className={`${dim} rounded border border-slate-300 shadow-sm`} style={{ background: value || "#003E87" }} />
      {open && pos && createPortal(
        <div ref={popRef} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
          data-testid={testid ? `${testid}-popover` : undefined}
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          className="z-[200] p-2 bg-white rounded-lg border border-slate-200 shadow-xl w-[188px]">
          <HexColorPicker color={draft} onChange={setDraft} style={{ width: "100%", height: 130 }} />
          <div className="flex items-center gap-1 mt-2">
            <span className="text-[11px] text-slate-400 font-mono">#</span>
            <input value={(draft || "").replace(/^#/, "")}
              onChange={(e) => setDraft("#" + e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6))}
              className="flex-1 text-xs border border-slate-200 rounded px-1.5 py-1 font-mono uppercase" data-testid={testid ? `${testid}-hex` : undefined} />
            <button type="button" onClick={() => { commit(); setOpen(false); }} className="text-[11px] px-2 py-1 rounded bg-[#003E87] text-white" data-testid={testid ? `${testid}-done` : undefined}>Done</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {COLORS.map((c) => (
              <button key={c} type="button" onClick={() => { setDraft(c); onChange(c); setOpen(false); }}
                className={`w-4 h-4 rounded-sm border ${(value || "").toLowerCase() === c.toLowerCase() ? "ring-2 ring-slate-400" : "border-slate-200"}`}
                style={{ background: c }} data-testid={testid ? `${testid}-swatch-${c}` : undefined} />
            ))}
          </div>
        </div>, (document.getElementById("takeoff-fs-root") || document.body))}
    </div>
  );
}
